import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, Select, DatePicker, StatusChip } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/keys.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';
import { ResponsiveTable } from '../../components/Table/ResponsiveTable.js';

const STATUSES = [
  'PENDING_KYC',
  'PENDING_PAYMENT',
  'PAYMENT_RECEIVED',
  'PROCESSING',
  'PAID_OUT',
  'COMPLETED',
  'CANCELLED',
  'FAILED',
  'REFUNDED',
];

interface Row {
  id: string;
  reference_code: string;
  user_id: string;
  from_currency: string;
  to_currency: string;
  amount_send: number;
  amount_receive: number;
  status: string;
  created_at: string;
  sender_first_name: string | null;
  sender_last_name: string | null;
  sender_email: string | null;
  beneficiary_name: string | null;
  payout_method: 'mobile_money' | 'bank' | null;
}

function senderName(t: (k: string) => string, r: Row) {
  const name = [r.sender_first_name, r.sender_last_name].filter(Boolean).join(' ').trim();
  return name || r.sender_email || t('transfers.unknownCustomer');
}

function recipientName(t: (k: string) => string, r: Row) {
  return r.beneficiary_name?.trim() || t('transfers.noBeneficiary');
}

export function TransfersListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ status: '', since: '', until: '' });
  const transfersQ = useQuery({
    queryKey: qk.transfers(filters),
    queryFn: () =>
      callFunction<{ items: Row[]; total: number }>('admin-transfers-list', {
        query: {
          status: filters.status || undefined,
          since: filters.since || undefined,
          until: filters.until || undefined,
        },
      }),
  });

  const items = transfersQ.data?.items ?? [];

  const columns = [
    {
      key: 'parties',
      label: t('transfers.parties'),
      render: (tr: Row) => (
        <div className="flex flex-col">
          <span className="font-semibold text-text">
            {senderName(t, tr)} → {recipientName(t, tr)}
          </span>
          <span className="font-mono text-xs text-text-muted">{tr.reference_code}</span>
        </div>
      ),
    },
    {
      key: 'amount',
      label: t('transfers.amount'),
      render: (tr: Row) => (
        <span className="font-mono whitespace-nowrap">
          {tr.amount_send} {tr.from_currency} → {tr.amount_receive} {tr.to_currency}
        </span>
      ),
    },
    {
      key: 'method',
      label: t('transfers.method'),
      render: (tr: Row) =>
        tr.payout_method ? (
          <span>{t(`transfers.method_label.${tr.payout_method}`)}</span>
        ) : (
          <span className="text-text-muted">—</span>
        ),
    },
    {
      key: 'status',
      label: t('transfers.status'),
      render: (tr: Row) => <StatusChip status={tr.status} />,
    },
    {
      key: 'created',
      label: t('transfers.created'),
      hideOnMobile: true,
      render: (tr: Row) => (
        <span className="text-text-muted">
          {new Date(tr.created_at).toLocaleString(
            i18n.language === 'fr' ? 'fr-FR' : 'en-US',
          )}
        </span>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('transfers.title')}</CardTitle>
      </CardHeader>
      <div className="mb-4 flex flex-col gap-3 px-5 md:flex-row">
        <div className="flex-1 space-y-1">
          <label className="text-xs font-semibold text-text-muted md:hidden">
            {t('transfers.status')}
          </label>
          <Select
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            placeholder={t('common.all')}
            options={STATUSES.map((s) => ({
              value: s,
              label: t(`transfers.status_label.${s}`),
            }))}
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className="text-xs font-semibold text-text-muted md:hidden">
            {t('transfers.filters.since')}
          </label>
          <DatePicker
            value={filters.since}
            onChange={(v) => setFilters((f) => ({ ...f, since: v }))}
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className="text-xs font-semibold text-text-muted md:hidden">
            {t('transfers.filters.until')}
          </label>
          <DatePicker
            value={filters.until}
            onChange={(v) => setFilters((f) => ({ ...f, until: v }))}
          />
        </div>
      </div>

      {transfersQ.isLoading ? (
        <LoadingScreen />
      ) : transfersQ.isError ? (
        <ErrorState onRetry={() => transfersQ.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={t('transfers.empty')} />
      ) : (
        <div className="px-5 pb-5">
          <ResponsiveTable
            columns={columns}
            data={items}
            keyExtractor={(tr) => tr.id}
            onRowClick={(tr) => navigate(`/transfers/${tr.id}`)}
            renderMobileHeader={(tr) => (
              <div className="flex flex-col">
                <span className="font-semibold text-text">
                  {senderName(t, tr)} → {recipientName(t, tr)}
                </span>
                <span className="font-mono text-xs text-text-muted">{tr.reference_code}</span>
              </div>
            )}
            emptyMessage={t('transfers.empty')}
          />
        </div>
      )}
    </Card>
  );
}
